import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ApiError } from "../../api/client";
import {
  localAccountsQuery,
  type LocalAccountRow,
  type LocalAccountsOverview,
} from "../../api/securiteeLocalAccounts";
import { escapeHtml } from "../../inventoree/format";
import { Content } from "../../ui/Shell";
import {
  DataTable,
  useTableState,
  type TableColumn,
  type TableState,
} from "../../ui/DataTable";
import { Spinner } from "../../ui/Spinner";
import "./local-accounts.css";

/**
 * Securitee > Local accounts: conturile locale ale masinilor Windows, citite de
 * agent. Numai afisare. Lista vine intreaga; filtrul, cautarea si paginile se
 * fac aici. Cifrele si constatarile vin de pe server (`LocalAccountRules`).
 */
export const TABLE_ID = "securiteeLocalAccounts";

type Filter = "" | "unused" | "builtin" | "neverExpires";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "", label: "All" },
  { key: "unused", label: "Unused" },
  { key: "builtin", label: "Built-in on" },
  { key: "neverExpires", label: "Never expires" },
];

const BUILTIN: Record<string, string> = {
  administrator: "Administrator",
  guest: "Guest",
  system: "System",
};

export function LocalAccountsPage() {
  const page = useQuery(localAccountsQuery());
  const [filter, setFilter] = useState<Filter>("");
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    order: { column: 0, dir: "asc" },
    search: "",
  });
  const data = page.data;
  const shown = useMemo(
    () => pageRows(data?.rows ?? [], filter, state),
    [data?.rows, filter, state],
  );

  if (page.error) {
    return (
      <Content>
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i>{" "}
          {page.error instanceof ApiError && page.error.status === 403
            ? "You do not have access to this action."
            : "The local accounts could not be read."}
        </div>
      </Content>
    );
  }
  if (!data) return <Spinner />;

  const pick = (next: Filter) => {
    setFilter(next);
    setState({ start: 0 });
  };

  return (
    <Content>
      <div id="content" className="la-page">
        <div className="la-top">
          <header className="panel-heading la-head">
            <h3>
              <strong>Local accounts</strong>
            </h3>
            <label className="color">
              Local accounts of the Windows machines, read by the OptimumDesk
              agent · read only
            </label>
          </header>

          {!data.ready ? (
            <p className="la-empty">
              This is not collected on this installation.
            </p>
          ) : data.rows.length === 0 ? (
            <p className="la-empty">
              No Windows machine has sent its local accounts yet (it needs a
              newer agent).
            </p>
          ) : (
            <>
              <Counters counts={data.counts} />
              <div
                className="la-chips btn-group btn-group-sm"
                role="group"
                aria-label="Show"
              >
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={
                      "btn " +
                      (filter === f.key ? "btn-primary" : "btn-default")
                    }
                    aria-pressed={filter === f.key}
                    onClick={() => pick(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {data.ready && data.rows.length > 0 && (
          /* pe telefon tabelul se deruleaza in el, nu toata pagina */
          <div className="la-table-scroll">
            <DataTable
              id={TABLE_ID}
              columns={COLUMNS}
              rows={shown.rows}
              total={shown.total}
              fetching={page.isFetching}
              state={state}
              onState={setState}
              searchHint="Machine or account"
              rowId={(r) => `la_${String(r.deviceId)}_${String(r.name)}`}
            />
          </div>
        )}
      </div>
    </Content>
  );
}

function Counters({ counts }: { counts: LocalAccountsOverview["counts"] }) {
  const items: { title: string; value: number; finding: boolean }[] = [
    { title: "Enabled accounts", value: counts.enabled, finding: false },
    { title: "Unused 90 days", value: counts.unused, finding: true },
    {
      title: "Built-in Administrator / Guest on",
      value: counts.builtin,
      finding: true,
    },
    {
      title: "Password never expires",
      value: counts.neverExpires,
      finding: true,
    },
  ];
  return (
    <div className="la-counters">
      {items.map((c) => (
        <div
          key={c.title}
          className={
            "la-counter" + (c.finding && c.value > 0 ? " la-counter-bad" : "")
          }
        >
          <span className="la-counter-title">{c.title}</span>
          <span className="la-counter-value">{c.value}</span>
        </div>
      ))}
    </div>
  );
}

/* Dupa ce se sorteaza fiecare coloana; masina e departajata de cont. */
const SORT: ((r: LocalAccountRow) => string | number)[] = [
  (r) => r.machine.toLowerCase(),
  (r) => r.name.toLowerCase(),
  (r) => (r.enabled ? 0 : 1),
  (r) => r.builtin ?? "",
  (r) => r.lastLogon ?? "",
  (r) => r.passwordAgeDays,
];

/** Filtrul, cautarea pe masina si cont, ordinea si pagina, pe randurile aduse. */
function pageRows(
  rows: LocalAccountRow[],
  filter: Filter,
  state: TableState,
): { rows: Record<string, unknown>[]; total: number } {
  const q = state.search.trim().toLowerCase();
  const kept = rows.filter(
    (r) =>
      (filter === "" ||
        (filter === "unused" && r.unused) ||
        (filter === "builtin" && r.builtinEnabled) ||
        (filter === "neverExpires" && r.neverExpires)) &&
      (q === "" || `${r.machine} ${r.name}`.toLowerCase().includes(q)),
  );
  const key = SORT[state.order.column] ?? SORT[0]!;
  const dir = state.order.dir === "desc" ? -1 : 1;
  const cmp = (a: string | number, b: string | number) =>
    typeof a === "number" && typeof b === "number"
      ? a - b
      : String(a).localeCompare(String(b));
  const sorted = [...kept].sort(
    (a, b) =>
      dir * cmp(key(a), key(b)) ||
      cmp(SORT[0]!(a), SORT[0]!(b)) ||
      cmp(SORT[1]!(a), SORT[1]!(b)),
  );
  return {
    total: sorted.length,
    rows: sorted.slice(state.start, state.start + state.length),
  };
}

const COLUMNS: TableColumn[] = [
  {
    index: 0,
    title: "Machine",
    orderable: true,
    visible: true,
    html: (r) => escapeHtml(r.machine),
  },
  {
    index: 1,
    title: "Account",
    orderable: true,
    visible: true,
    html: (r) => `<strong>${escapeHtml(r.name)}</strong>`,
  },
  {
    index: 2,
    title: "Status",
    orderable: true,
    visible: true,
    width: 90,
    html: (r) =>
      r.enabled ? "Enabled" : '<span class="la-muted">Disabled</span>',
  },
  {
    index: 3,
    title: "Built-in",
    orderable: true,
    visible: true,
    width: 110,
    html: (r) =>
      typeof r.builtin === "string" && BUILTIN[r.builtin]
        ? escapeHtml(BUILTIN[r.builtin])
        : '<span class="la-muted">-</span>',
  },
  {
    index: 4,
    title: "Last sign-in",
    orderable: true,
    visible: true,
    width: 120,
    // ora serverului, nu UTC
    html: (r) =>
      typeof r.lastLogon === "string" && r.lastLogon !== ""
        ? `<span title="${escapeHtml(r.lastLogon)}">${escapeHtml(r.lastLogon.slice(0, 10))}</span>`
        : '<span class="la-muted">never</span>',
  },
  {
    index: 5,
    title: "Password",
    orderable: true,
    visible: true,
    width: 190,
    html: (r) =>
      `${Number(r.passwordAgeDays)} days` +
      (r.neverExpires
        ? ' <span class="label label-warning la-flag">never expires</span>'
        : ""),
  },
];
